import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import apiClient from "../../services/apiClient";
import DynamicTable from "../../components/Table/DataTable";
import TablePaginationActions from "../../components/Table/tablePagination";
import { X } from "lucide-react";

const MasterDetails: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();

    const [masterDetails, setMasterDetails] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [totalCount, setTotalCount] = useState(0);
    const [searchKey, setSearchKey] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [orderBy, setOrderBy] = useState("id");
    const [orderDir, setOrderDir] = useState(1);

    const fetchMasterDetail = async () => {
        if (!slug) return;
        try {
            setLoading(true);
            setError("");
            const offset = page * rowsPerPage;
            const res = await apiClient<any>(
                `v1/resource/masters?slug=${slug}&searchkey=${searchKey}&order_by=${orderBy}&dir=${orderDir}&limit=${rowsPerPage}&page=${page + 1}`
            );
            const masters = res?.data?.masters || [];
            setMasterDetails(masters);
            console.log("Fetched Masters:", JSON.stringify(masters, null, 2));
            setTotalCount(res?.data?.pagination?.total || 0);
        } catch (err: any) {
            console.error(err);
            setError("Failed to fetch master details.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMasterDetail();
    }, [slug, page, rowsPerPage, searchKey, orderBy, orderDir]);

    const handlePageChange = (newPage: number) => setPage(newPage);
    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        setRowsPerPage(newRowsPerPage);
        setPage(0);
    };

    // Gather all unique attribute keys across rows
    const attributeKeys = Array.from(
        new Set(
            masterDetails.flatMap((row) =>
                row.attributes && typeof row.attributes === "object"
                    ? Object.keys(row.attributes)
                    : []
            )
        )
    );
    const handleSearchClear = () => {
        setSearchKey("");
        setPage(0); // Reset to first page on clear
    };
    // Determine if Color column is needed
    const hasColorColumn = attributeKeys.includes("bg_color") || attributeKeys.includes("text_color");

    // Dynamic columns
    // Inside the dynamic column generation part

    // Dynamic columns
    const columns = [
        {
            key: "serial",
            label: "S.No",
            render: (_row: any, index: number) => page * rowsPerPage + index + 1,
        },
        { key: "name", label: "Name", align: "center" },
        { key: "slug", label: "Slug", align: "center" },
        {
            key: "status",
            label: "Status",
            align: "center",
            render: (row: any) => (row.is_active ? "Active" : "Inactive"),
        },
        ...(hasColorColumn
            ? [
                {
                    key: "color",
                    label: "Color",
                    align: "center",
                    render: (row: any) => {
                        if (row.attributes?.bg_color || row.attributes?.text_color) {
                            const bg = row.attributes.bg_color || "#ffffff";
                            const text = row.attributes.text_color || "#000000";
                            return (
                                <span
                                    style={{
                                        display: "inline-block",
                                        padding: "4px 10px",
                                        borderRadius: "12px",
                                        backgroundColor: bg,
                                        color: text,
                                        fontWeight: 600,
                                        fontSize: "0.85rem",
                                        textAlign: "center",
                                    }}
                                >
                                    bg: {bg}, text: {text}
                                </span>
                            );
                        }
                        return null;
                    },
                },
            ]
            : []),
        ...attributeKeys
            .filter((key) => key !== "bg_color" && key !== "text_color")
            .map((key) => ({
                key,
                label: key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
                align: "center",
                render: (row: any) => {
                    const val = row.attributes?.[key];

                    if (val === null || val === undefined) return <span style={{ color: "#888" }}>N/A</span>;

                    // Array of strings
                    if (Array.isArray(val) && typeof val[0] === "string") {
                        return (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", justifyContent: "center" }}>
                                {val.map((item, idx) => (
                                    <span
                                        key={idx}
                                        style={{
                                            padding: "2px 6px",
                                            backgroundColor: "#e0e0e0",
                                            borderRadius: "12px",
                                            fontSize: "0.75rem",
                                        }}
                                    >
                                        {item}
                                    </span>
                                ))}
                            </div>
                        );
                    }

                    // Array of objects (extract 'name' if exists)
                    if (Array.isArray(val) && typeof val[0] === "object") {
                        return (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", justifyContent: "center" }}>
                                {val.map((item: any, idx: number) => (
                                    <span
                                        key={idx}
                                        style={{
                                            padding: "2px 6px",
                                            backgroundColor: "#e0e0e0",
                                            borderRadius: "12px",
                                            fontSize: "0.75rem",
                                        }}
                                    >
                                        {item.name || JSON.stringify(item)}
                                    </span>
                                ))}
                            </div>
                        );
                    }

                    // Nested object → show JSON
                    if (typeof val === "object") {
                        return (
                            <pre
                                style={{
                                    maxWidth: "200px",
                                    overflowX: "auto",
                                    whiteSpace: "pre-wrap",
                                    wordWrap: "break-word",
                                    fontSize: "0.75rem",
                                    background: "#f0f0f0",
                                    padding: "4px 6px",
                                    borderRadius: "6px",
                                }}
                            >
                                {JSON.stringify(val, null, 2)}
                            </pre>
                        );
                    }

                    // Simple string / number
                    return val;
                },
            })),

    ];


    return (
        <Layout>
            <div style={{ padding: "20px" }}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "20px",
                        marginBottom: "25px",
                        padding: "15px 25px",
                        background: "linear-gradient(135deg, #e3f2fd, #ffffff)",
                        borderRadius: "12px",
                        boxShadow: "0 6px 15px rgba(0,0,0,0.1)",
                        transition: "transform 0.2s, box-shadow 0.2s",
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.15)";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "0 6px 15px rgba(0,0,0,0.1)";
                    }}
                >
                    <button
                        onClick={() => navigate(-1)}
                        style={{
                            background: "linear-gradient(90deg, #42a5f5, #1976d2)",
                            color: "#fff",
                            border: "none",
                            borderRadius: "8px",
                            padding: "8px 18px",
                            cursor: "pointer",
                            fontWeight: 600,
                            fontSize: "0.95rem",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                            transition: "all 0.3s",
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.transform = "scale(1.05)";
                            e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.25)";
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                            e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.15)";
                        }}
                    >
                        ← Back
                    </button>

                    <h2
                        style={{
                            margin: 0,
                            fontSize: "1.7rem",
                            color: "#0d47a1",
                            fontWeight: 700,
                            textShadow: "1px 1px 3px rgba(0,0,0,0.1)",
                            flex: 1,
                        }}
                    >
                        Master Detail - {slug}
                    </h2>

                    <div
                        style={{
                            position: "relative",
                            display: "flex",
                            alignItems: "center",
                            border: "1px solid #ccc",
                            borderRadius: "25px",
                            padding: "6px 12px",
                            width: "280px",
                            backgroundColor: "#f9f9f9",
                            transition: "all 0.3s",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = "#1976d2";
                            e.currentTarget.style.boxShadow = "0 2px 8px rgba(25, 118, 210, 0.2)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = "#ccc";
                            e.currentTarget.style.boxShadow = "none";
                        }}
                    >
                        <span
                            style={{
                                marginRight: "8px",
                                color: "#1976d2",
                                fontSize: "1.1rem",
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            🔍
                        </span>
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchKey}
                            onChange={(e) => {
                                setSearchKey(e.target.value);
                                setPage(0);
                            }}
                            style={{
                                border: "none",
                                outline: "none",
                                flex: 1,
                                fontSize: "14px",
                                backgroundColor: "transparent",
                            }}
                        />
                        {searchKey && (
                            <button
                                onClick={handleSearchClear}
                                style={{
                                    position: "absolute",
                                    right: "12px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    color: "#888",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "2px",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.color = "#666";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.color = "#888";
                                }}
                            >
                                <X size={18} />
                            </button>
                        )}
                    </div>
                </div>

                <DynamicTable
                    columns={columns}
                    data={masterDetails}
                    loading={loading}
                    emptyMessage={"No master details found"}
                    page={page}
                    error={error}
                    rowsPerPage={rowsPerPage}
                    totalCount={totalCount}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    ActionsComponent={TablePaginationActions}
                />
            </div>
        </Layout>
    );
};

export default MasterDetails;
